// Componente de gráfico donut interactivo con tooltips usando Recharts
// Interactive donut chart component with tooltips using Recharts

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

interface DonutSegment {
  id: string;
  label: string;
  value: number;
  color: string;
  percentage: number;
}

interface DonutChartProps {
  data: DonutSegment[];
  total: number;
  size?: number;
}

export const DonutChart = ({ data, total, size = 200 }: DonutChartProps) => {
  const { t } = useTranslation('homeModule');
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  // Preparar datos para Recharts
  const chartData = data.map((segment) => ({
    name: segment.label,
    value: segment.value,
    color: segment.color,
    percentage: segment.percentage,
  }));

  // Custom tooltip con z-index alto
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div
          className="bg-card-bg border border-card-border rounded-md shadow-lg p-3 z-50 w-44"
          style={{ zIndex: 999999 }}
        >
          <div className="flex items-center gap-2">
            <div
              className="w-3 h-3 rounded-sm"
              style={{ backgroundColor: data.color }}
            />
            <div>
              <p className="font-medium text-text">{data.name}</p>
              <p className="text-sm text-subtext">
                {data.value} ({data.percentage.toFixed(1)}%)
              </p>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="flex flex-col items-center">
      <div className="relative" style={{ width: size, height: size }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              innerRadius={size * 0.38} // 40% del tamaño para el "hueco" del donut
              outerRadius={size * 0.48} // 48% del tamaño para dejar espacio al hover
              paddingAngle={0} // Sin separación entre segmentos
              dataKey="value"
              isAnimationActive={false} // Deshabilitar animaciones
              stroke="none" // Eliminar bordes
              onMouseEnter={(_, index) => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              {chartData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={entry.color}
                  stroke="none"
                  style={{
                    transform:
                      hoveredIndex === index ? 'scale(1.03)' : 'scale(1)',
                    transformOrigin: 'center',
                    transition: 'transform 0.2s ease-in-out',
                  }}
                />
              ))}
            </Pie>
            <Tooltip
              content={<CustomTooltip />}
              offset={30}
              position={{ x: 0, y: -60 }}
              wrapperStyle={{ pointerEvents: 'none' }}
            />
          </PieChart>
        </ResponsiveContainer>

        {/* Contenido central */}
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none z-0">
          <span className="text-2xl font-bold text-title">{total}</span>
          <span className="text-xs text-subtext">
            {t('errors.total_errors')}
          </span>
        </div>
      </div>
    </div>
  );
};
